import DocumentEditor from "@txtextcontrol/tx-react-document-editor";
import { Button, Card, DatePicker, Input, Select, Row, Col, Skeleton, Modal } from "antd";
import {
  getJobDetails,
  loadDocument,
  loadVoiceFiles,
  lockJob,
  saveDocument,
  saveJob,
  updateJob,
  updateLockJob
} from "api/services/JobService";
import {
  TJobDetails,
  TVoiceFile,
  TTextControlJobDetails,
  JobStatus,
  PrintDownloadStatus,
  ApprovedStatus
} from "api/services/models/types";
import { WebDPrivilege, WebDUser } from "api/services/models/WebDUser";
import { loadVoiceFile } from "api/services/VoiceService";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { usePicklists } from "components/picklists/PicklistContextProvider";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { ReactComponent as AddIcon } from "../../Icons/vuesax/add.svg";
import { ReactComponent as CalenderIcon } from "../../Icons/vuesax/calendar.svg";
import { ReactComponent as DocumentCloudIcon } from "../../Icons/vuesax/document-cloud.svg";
import { ReactComponent as TickSquareIcon } from "../../Icons/vuesax/tick-square.svg";
import AudioPlayer from "./AudioPlayer";
import { showErrorToast } from "helpers/utils";
import { usePolling } from "helpers/usePolling";
interface DocumentEditorCardProps {
  source: string;
  isVisible: boolean;
  handleClose: (
    jobId: number | null,
    lock: boolean,
    jobApproved: boolean,
    jobDetail: TJobDetails | null
  ) => void;
  selectedRowData: any;
  editMode: string;
}

const DocumentEditorCard: React.FC<DocumentEditorCardProps> = ({
  isVisible,
  handleClose,
  selectedRowData,
  source,
  editMode
}) => {
  if (!isVisible) {
    return null;
  }

  const { TextArea } = Input;
  const isListenerAdded = useRef(false);
  const { documentTypes, locations } = usePicklists();
  const [selectedReportTypeIds, setSelectedReportTypeIds] = useState(
    selectedRowData.reportType
  );
  const [selectedLocationIds, setSelectedLocationIds] = useState(selectedRowData.location);
  const [voiceFiles, setVoiceFiles] = useState<TVoiceFile[]>([]);
  const [jobDetail, setJobDetails] = useState<TJobDetails | null>();
  const [selectedAudioFile, setAudioFile] = useState<string | Blob>("/resources/Default.wav");
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedFileId, setSelectedFileId] = useState<number | null>();
  const [sessionId, setSessionId] = useState<string>("");
  const [isJobDetailLoaded, setIsJobDetailLoaded] = useState<boolean>(false);
  const [cardLoading, setCardLoading] = useState<boolean>(false);
  const [isTextControlLoaded, setIsTextControlLoaded] = useState<boolean>(false);
  const { accountId, webDUser } = useTDAuthentication();
  const userId = webDUser?.user.userId;
  const [textControlJobDetails, setTextControlJobDetails] =
    useState<TTextControlJobDetails | null>({
      jobIds: [],
      accountId: "",
      userId: "",
      jobAction: "",
      connectionId: null
    });
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [readOnly, setReadOnly] = useState<boolean>(true);
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const { start, stop, polling } = usePolling(autoSaveDocument, 2000);

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isDocumentEditDisabled = (source: string) => {
    switch (source) {
      case "Inbox":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsModifyInbox);
      case "QCHold":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsModifyQCHold);
      case "Archive":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsModifyArchive);
      default:
        return true;
    }
  };

  const isDocumentDemographicEditDisabled = (source: string) => {
    switch (source) {
      case "Inbox":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsModifyDemographicsInbox);
      case "QCHold":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsModifyDemographicsQCHold);
      case "Archive":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsModifyDemographicsArchive);
      default:
        return true;
    }
  };

  const isReleaseDisabled = (source: string) => {
    switch (source) {
      case "Inbox":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsReleaseQCHold);
      case "QCHold":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsReleaseQCHold);
      case "Archive":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsReleaseQCHold);
      default:
        return true;
    }
  };

  const isApproveDisabled = (source: string) => {
    switch (source) {
      case "Inbox":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsApproveInbox);
      case "QCHold":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsApproveInbox);
      case "Archive":
        return !webDUser?.hasPrivilege(WebDPrivilege.DocumentsApproveArchive);
      default:
        return true;
    }
  };

  useEffect(() => {
    try {
      if (!(isJobDetailLoaded && isTextControlLoaded && sessionId !== "")) return;

      const updateJobLock = async () => {
        try {
          await updateLockJob(selectedRowData?.jobId, sessionId, accountId ?? "");
        } catch (error) {
          console.error("Error updating job lock:", error);
        }
      };

      updateJobLock();

      const intervalId = setInterval(() => {}, 60000);

      return () => clearInterval(intervalId);
    } catch (error) {
      console.log("lock could not be updated");
    }
  }, [isJobDetailLoaded, isTextControlLoaded, sessionId, selectedRowData]);

  useEffect(() => {
    setReadOnly(selectedRowData.approved === ApprovedStatus.Yes);
    setIsDisabled(isDocumentDemographicEditDisabled(source));
    const loadVoiceFileList = async () => {
      const loadedVoiceFiles = await loadVoiceFiles(selectedRowData.jobId, accountId ?? "");
      setVoiceFiles(loadedVoiceFiles);
    };
    setLoading(true);
    try {
      loadVoiceFileList();
      loadJobDetails();
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAudioFile = async (vfId: number) => {
    if (!vfId) {
      return;
    }
    const loadedAudioFile = await loadVoiceFile(vfId);
    setAudioFile(loadedAudioFile);
  };

  const loadJobDetails = async () => {
    const jobDetail = await getJobDetails(selectedRowData.jobId, accountId || "");
    setJobDetails(jobDetail);
    setIsJobDetailLoaded(true);
  };

  useEffect(() => {
    loadAudioFile(selectedFileId ?? 0);
  }, [selectedFileId]);

  useEffect(() => {
    setSelectedFileId(jobDetail?.vfId);
  }, [jobDetail]);

  const handleChange = (value: any) => {
    setSelectedFileId(value);
    setJobDetails({ ...jobDetail, vfId: value });
  };

  const handleDocumentLoaded = () => {
    console.log("Document loaded");
  };

  function autoSaveDocument() {
    console.log("autosave reachged");
    //@ts-ignore
    if (selectedRowData && window.TXTextControl) {
      const storageKey = selectedRowData?.jobId
        ? String(selectedRowData?.jobId + userId)
        : "defaultDocument";
      // store the document in the session storage
      // which is shared over all browser tabs (complete session)
      //@ts-ignore
      window.TXTextControl.saveDocument(
        //@ts-ignore
        window.TXTextControl.streamType.InternalUnicodeFormat,
        function (e: any) {
          sessionStorage.setItem(storageKey, e.data);
        }
      );
    }
  }

  function restoreDocument() {
  // Use the same key as in autoSaveDocument
  const storageKey = selectedRowData?.jobId
    ? String(selectedRowData?.jobId + userId)
    : "defaultDocument";
  const savedDocument = sessionStorage.getItem(storageKey);

  if (savedDocument) {
    const userConfirmed = window.confirm(
      "There seems to be pending changes that have not been saved, would you like to load from the memory?"
    );
    if (userConfirmed) {
      // @ts-ignore
      window.TXTextControl.loadDocument(
        // @ts-ignore
        window.TXTextControl.streamType.InternalUnicodeFormat,
        savedDocument
      );
      start();
    } else {
      checkAndLoadDocument();
      start();
    }
  } else {
    checkAndLoadDocument();
    start();
  }
}

  function onTextControlLoaded(e: any) {
    setIsTextControlLoaded(true);
    // disable the loading progress dialog
    //@ts-ignore
    window.TXTextControl.isLoadingDialogEnabled = false;
    // set auto save to 500 milliseconds
    // window.setInterval(autoSaveDocument, 500);
  }
  function onRibbonTabLoaded() {
    console.log("TXTextControl RibbonTabs loaded.");
    //@ts-ignore
    window.TXTextControl.setIsTrackChangesEnabled(true);
    //@ts-ignore
    document.getElementById("ribbon").style.display = "none";
  }
  const handleLoaded = async () => {
    //@ts-ignore
    if (window.TXTextControl) {
      //@ts-ignore
      window.TXTextControl.addEventListener("textControlLoaded", onTextControlLoaded);
      //@ts-ignore
      window.TXTextControl.addEventListener("webSocketClosed", function (e) {
        // reload, if connection is lost
        location.reload();
      });

      //@ts-ignore
      window.TXTextControl.addEventListener("documentLoaded", handleDocumentLoaded);
      //@ts-ignore
      window.TXTextControl.addEventListener("ribbonTabsLoaded", onRibbonTabLoaded);
      //@ts-ignore
      window.TXTextControl.clipboardMode = 1;
      if (readOnly) {
        //@ts-ignore
        window.TXTextControl.editMode = 1;
      }
      isListenerAdded.current = true;
    }
  };

  const checkAndLoadDocument = async () => {
    if (isJobDetailLoaded && isTextControlLoaded) {
      const jobDetails: TTextControlJobDetails = {
        jobIds: [`${selectedRowData?.jobId}`],
        accountId: `${selectedRowData?.accountId}`,
        userId: userId ?? "",
        jobAction: "Open",
        //@ts-ignore
        connectionId: window.TXTextControl.connectionID
      };
      setTextControlJobDetails(jobDetails);
    const handleCheckAndLoadDocument = async () => {
      try {
        const sessionID = await lockJob(selectedRowData?.jobId, accountId ?? "");
        setSessionId(sessionID);
      } catch (err: any) {
        showErrorToast("Document is being accesesed by another user");
        await handleTextControlAndClose(selectedRowData?.jobId, true);
      }
      await loadDocument(jobDetails);
    };
      const storageKey = selectedRowData?.jobId
        ? String(selectedRowData?.jobId + userId)
        : "defaultDocument";
      const savedDocument = sessionStorage.getItem(storageKey);
      //@ts-ignore
      if (savedDocument && window.TXTextControl) {
        const userConfirmed = window.confirm(
          "There seems to be pending changes that have not been saved, would you like to load from the memory?"
        );
        if (userConfirmed) {
          // @ts-ignore 
          console.log(window.TXTextControl.streamType.InternalUnicodeFormat);
          // @ts-ignore 
          window.TXTextControl.loadDocument(window.TXTextControl.streamType.InternalUnicodeFormat, savedDocument);
          start();
        } else {
          await handleCheckAndLoadDocument();
          start();
        }
      } else {
        await handleCheckAndLoadDocument();
        start();
      }
    }
  };

  function formatTimestamp(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true
    };
    return date.toLocaleString("en-US", options);
  }

  function removeAllChanges(accept: boolean) {
    //@ts-ignore
    if (window.TXTextControl) {
      //@ts-ignore
      window.TXTextControl.trackedChanges.getCount(count => {
        if (count === 0) return;

        // recursively loop through all changes
        // and remove them
        //@ts-ignore
        window.TXTextControl.trackedChanges.elementAt(0, element => {
          //@ts-ignore
          window.TXTextControl.trackedChanges.remove(element, accept, deleted => {
            if (deleted === true) removeAllChanges(accept);
          });
        });
      });
    }
  }
  function deleteSavedDocument() {
    if (selectedRowData && userId) {
      const storageKey = selectedRowData?.jobId
        ? String(selectedRowData?.jobId + userId)
        : "defaultDocument";
      sessionStorage.removeItem(storageKey);
    }
  }

  const handleTextControlAndClose = async (
    jobId: number | null,
    lock: boolean,
    jobApproved: boolean = false
  ) => {
    stop();
    //@ts-ignore
    if (window.TXTextControl) {
      //@ts-ignore
      window.TXTextControl.removeEventListener("documentLoaded", handleDocumentLoaded);//@ts-ignore
      window.TXTextControl.removeEventListener("textControlLoaded", onTextControlLoaded);//@ts-ignore
      isListenerAdded.current = false;
      //@ts-ignore
      window.TXTextControl.removeFromDom();
    }

    handleClose(jobId, lock, jobApproved, jobDetail ?? null);
  };

  const Approve = async () => {
    if (isApproveDisabled(source)) {
      showErrorToast("Permission Denied");
      return;
    }
    if (!jobDetail) {
      console.error("Job details are required.");
      return;
    }

    if (!jobDetail.reportType) {
      showErrorToast("Report Type cannot be empty");
      return;
    }

    if (!textControlJobDetails) {
      console.error("Job details are missing.");
      return;
    }
    removeAllChanges(true);
    await updateJob(selectedRowData?.jobId, jobDetail, accountId ?? "");
    await saveJob(selectedRowData?.jobId, sessionId, "Approve", jobDetail, accountId ?? "");
    if (jobDetail?.isEsignEnabled) {
      var currentDate = new Date();
      var formattedTimestamp = formatTimestamp(currentDate);
      var additionalString =
        "This Document was electronically signed by " +
        jobDetail?.providerSignature +
        " on " +
        formattedTimestamp;
      //@ts-ignore
      window.TXTextControl.selectAll();
      //@ts-ignore
      window.TXTextControl.selection.getLength(
        function (curSelLength: any) {
          // success
          //@ts-ignore
          window.TXTextControl.selection.setStart(curSelLength);
          //@ts-ignore
          window.TXTextControl.selection.setLength(0);
          //@ts-ignore
          window.TXTextControl.focus();
          //@ts-ignore
          window.TXTextControl.selection.load(
            //@ts-ignore
            window.TXTextControl.StreamType.PlainText,
            btoa(additionalString)
          );
        },
        function (errArg: any) {
          // error
          console.log(errArg);
        }
      );
    }
    setCardLoading(true);
    await saveDocument(textControlJobDetails);
    await handleTextControlAndClose(selectedRowData?.jobId, false, true);
    deleteSavedDocument();
    setCardLoading(false);
  };

  const Release = async () => {
    if (isReleaseDisabled(source)) {
      showErrorToast("Permission Denied");
      return;
    }
    if (!jobDetail) {
      console.error("Job details are required.");
      return;
    }

    if (!jobDetail.reportType) {
      showErrorToast("Report Type cannot be empty");
      return;
    }

    if (!textControlJobDetails) {
      console.error("Job details are missing.");
      return;
    }
    setCardLoading(true);
    removeAllChanges(true);
    await updateJob(selectedRowData?.jobId, jobDetail, accountId ?? "");
    await saveJob(selectedRowData?.jobId, sessionId, "Release", jobDetail, accountId ?? "");
    await saveDocument(textControlJobDetails);
    await handleTextControlAndClose(selectedRowData?.jobId, false, true);
    deleteSavedDocument();
    setCardLoading(false);
  };

  const Save = async () => {
    if (!jobDetail) {
      console.error("Job details are required.");
      return;
    }

    if (!jobDetail.reportType) {
      showErrorToast("Report Type cannot be empty");
      return;
    }

    if (!textControlJobDetails) {
      console.error("Job details are missing.");
      return;
    }
    setCardLoading(true);
    removeAllChanges(true);
    await updateJob(selectedRowData?.jobId, jobDetail, accountId ?? "");
    await saveJob(
      selectedRowData?.jobId,
      sessionId,
      "SaveAndClose",
      jobDetail,
      accountId || ""
    );
    await saveDocument(textControlJobDetails);
    await handleTextControlAndClose(selectedRowData?.jobId, false);
    deleteSavedDocument();
    setCardLoading(false);
  };

  useEffect(() => {
    checkAndLoadDocument();
  }, [isJobDetailLoaded, isTextControlLoaded]);

  const closeCard = async () => {
    setCardLoading(true);
    if (!jobDetail) {
      console.error("Job details are required.");
      return;
    }

    if (!textControlJobDetails) {
      console.error("Job details are missing.");
      return;
    }

    const originalConsoleError = console.error;
    console.error = (...args) => {
      if (
        (args[0] &&
          typeof args[0] === "string" &&
          args[0].includes("Mssing ribbon tab header container. Cannot create ribbon tabs.")) ||
        args[0].includes("Cannot read properties of null (reading 'querySelector')") ||
        args[0].includes("Cannot read properties of null (reading 'removeChild')")
      ) {
        return;
      }
      originalConsoleError(...args);
    };

    await saveJob(selectedRowData?.jobId, sessionId, "Cancel", jobDetail, accountId || "");
    await handleTextControlAndClose(null, false);
    console.error = originalConsoleError;

    setCardLoading(false);
  };

  const validateAndConvertDate = (dateString: string | undefined) => {
    if (!dateString) {
      return null;
    }

    const parsedDate = dayjs(dateString, "MM-DD-YYYY", true);

    if (parsedDate.isValid()) {
      return parsedDate;
    } else {
      return null;
    }
  };

  const calculateDimensions = () => {
    const isLargeScreen = windowSize.width >= 1200;
    const isMediumScreen = windowSize.width >= 992 && windowSize.width < 1200;
    const isSmallScreen = windowSize.width >= 768 && windowSize.width < 992;
    const isXSmallScreen = windowSize.width < 768;

    let editorWidth = "100%";
    let editorHeight = "47.125rem";

    let sidebarWidth = "100%";
    let sidebarHeight = "auto";

    let editorSpan = 16;
    let sidebarSpan = 7;
    let useVerticalLayout = false;

    if (isXSmallScreen) {
      editorHeight = "35rem";
      useVerticalLayout = true;
      editorSpan = 24;
      sidebarSpan = 24;
    } else if (isSmallScreen) {
      editorHeight = "40rem";
      editorSpan = 14;
      sidebarSpan = 10;
    }

    return {
      editorWidth,
      editorHeight,
      sidebarWidth,
      sidebarHeight,
      editorSpan,
      sidebarSpan,
      useVerticalLayout
    };
  };

  const dimensions = calculateDimensions();

  const datePickerStyle = {
    width: "100%",
    position: "relative" as "relative",
    zIndex: 1
  };

  const modalCardStyle = {
    width: "95vw",
    maxWidth: "1400px",
    maxHeight: "90vh",
    overflowY: "auto" as "auto",
    margin: "0 auto"
  };

  return (
    <div
      className="modal-overlay"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000
      }}>
      <Card className="modal-card" styles={{ body: { padding: "0" } }} style={modalCardStyle}>
        {jobDetail && !cardLoading ? (
          <div style={{ padding: "20px" }}>
            <Row gutter={[16, 24]} justify="space-between" align="top">
              <Col xs={24} md={dimensions.editorSpan} style={{ padding: 0 }}>
                <DocumentEditor
                  style={{
                    width: dimensions.editorWidth,
                    height: dimensions.editorHeight,
                    maxWidth: "100%",
                    maxHeight: dimensions.editorHeight
                  }}
                  webSocketURL={process.env.REACT_APP_TEXTCONTROL_WEBSOCKET_URI}
                  editMode={editMode}
                  onLoad={handleLoaded}
                />
              </Col>
              <Col
                xs={24}
                md={dimensions.sidebarSpan}
                style={{
                  width: dimensions.sidebarWidth,
                  height: dimensions.sidebarHeight
                }}>
                <div
                  className="modal-actions"
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "15px"
                  }}>
                  <div className="button-container" style={{ display: "flex", gap: "10px" }}>
                    {!readOnly && (
                      <Button
                        type="primary"
                        onClick={
                          jobDetail.jobStatus === PrintDownloadStatus.Hold ? Release : Approve
                        }
                        disabled={isDocumentEditDisabled(source)}
                        className="approve-btn">
                        <TickSquareIcon width="16px" height="16px" />
                        {jobDetail.jobStatus === PrintDownloadStatus.Hold
                          ? "Release"
                          : "Approve"}
                      </Button>
                    )}
                    {!readOnly && (
                      <Button
                        disabled={isDocumentEditDisabled(source)}
                        onClick={Save}
                        className="save-btn">
                        <DocumentCloudIcon width="16px" height="16px" />
                        Save
                      </Button>
                    )}
                  </div>
                  <div
                    className="close-icon"
                    onClick={closeCard}
                    style={{
                      cursor: "pointer",
                      transform: "rotate(45deg)",
                      display: "flex",
                      alignItems: "center"
                    }}>
                    <AddIcon />
                  </div>
                </div>

                <AudioPlayer
                  src={selectedAudioFile}
                  className="audio-player"
                  style={{ width: "100%", marginBottom: "15px" }}
                />
                <Row gutter={[16, 20]} justify="space-between">
                  <Col xs={24}>
                    <label
                      htmlFor="audio-file"
                      className="input-label"
                      style={{ display: "block", marginBottom: "5px" }}>
                      Audio File
                    </label>
                    <Select
                      id="audio-file"
                      disabled={isDisabled || readOnly}
                      allowClear
                      className="input-field"
                      variant="borderless"
                      placeholder="Choose Audio File"
                      maxTagCount="responsive"
                      value={selectedFileId}
                      onChange={handleChange}
                      showSearch
                      loading={loading}
                      style={{ width: "100%" }}
                      filterOption={(input, option) =>
                        option?.props.children.toLowerCase().includes(input.toLowerCase())
                      }>
                      {voiceFiles.map(file => (
                        <Select.Option key={file.vfId} value={file.vfId}>
                          {file.voiceFileName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>

                  <Col xs={24} sm={8}>
                    <label
                      htmlFor="first-name"
                      className="input-label"
                      style={{ display: "block", marginBottom: "5px" }}>
                      First Name
                    </label>
                    <Input
                      disabled={isDisabled || readOnly}
                      id="first-name"
                      value={jobDetail?.firstName}
                      onChange={e =>
                        setJobDetails({ ...jobDetail, firstName: e.target.value })
                      }
                      className="input-field"
                      placeholder="First Name"
                      style={{ width: "100%" }}
                    />
                  </Col>

                  <Col xs={24} sm={8}>
                    <label
                      htmlFor="middle-name"
                      className="input-label"
                      style={{ display: "block", marginBottom: "5px" }}>
                      Middle Initial
                    </label>
                    <Input
                      id="middle-name"
                      disabled={isDisabled || readOnly}
                      value={jobDetail?.middleInitial}
                      onChange={e => {
                        const newValue = e.target.value;
                        if (newValue.length <= 1) {
                          setJobDetails({ ...jobDetail, middleInitial: newValue });
                        }
                      }}
                      className="input-field"
                      placeholder="Middle Initial"
                      style={{ width: "100%" }}
                    />
                  </Col>

                  <Col xs={24} sm={8}>
                    <label
                      htmlFor="last-name"
                      className="input-label"
                      style={{ display: "block", marginBottom: "5px" }}>
                      Last Name
                    </label>
                    <Input
                      id="last-name"
                      disabled={isDisabled || readOnly}
                      value={jobDetail?.lastName}
                      onChange={e =>
                        setJobDetails({ ...jobDetail, lastName: e.target.value })
                      }
                      className="input-field"
                      placeholder="Last Name"
                      style={{ width: "100%" }}
                    />
                  </Col>

                  <Col xs={24} sm={12}>
                    <label
                      htmlFor="dob"
                      className="input-label"
                      style={{ display: "block", marginBottom: "5px" }}>
                      DOB
                    </label>
                    <DatePicker
                      id="dob"
                      disabled={isDisabled || readOnly}
                      value={validateAndConvertDate(jobDetail?.patientDOB)}
                      onChange={date =>
                        setJobDetails({
                          ...jobDetail,
                          patientDOB: dayjs(date).format("MM-DD-YYYY")
                        })
                      }
                      className="input-field"
                      placeholder="MM/DD/YYYY"
                      format="MM/DD/YYYY"
                      suffixIcon={<CalenderIcon className="calendar-icon" />}
                      style={datePickerStyle}
                      getPopupContainer={trigger => trigger.parentElement || document.body}
                    />
                  </Col>

                  <Col xs={24} sm={12}>
                    <label
                      htmlFor="mrn"
                      className="input-label"
                      style={{ display: "block", marginBottom: "5px" }}>
                      MRN
                    </label>
                    <Input
                      id="mrn"
                      disabled={isDisabled || readOnly}
                      value={jobDetail?.mrn}
                      onChange={e => setJobDetails({ ...jobDetail, mrn: e.target.value })}
                      className="input-field"
                      placeholder="MRN"
                      style={{ width: "100%" }}
                    />
                  </Col>

                  <Col xs={24} sm={12}>
                    <label
                      htmlFor="date-of-service"
                      className="input-label"
                      style={{ display: "block", marginBottom: "5px" }}>
                      Date of service
                    </label>
                    <DatePicker
                      id="date-of-service"
                      disabled={isDisabled || readOnly}
                      value={validateAndConvertDate(jobDetail?.dateOfService)}
                      onChange={date =>
                        setJobDetails({
                          ...jobDetail,
                          dateOfService: dayjs(date).format("MM-DD-YYYY")
                        })
                      }
                      className="input-field"
                      placeholder="MM/DD/YYYY"
                      format="MM/DD/YYYY"
                      suffixIcon={<CalenderIcon className="calendar-icon" />}
                      style={datePickerStyle}
                      getPopupContainer={trigger => trigger.parentElement || document.body}
                    />
                  </Col>

                  <Col xs={24} sm={12}>
                    <label
                      htmlFor="account-number"
                      className="input-label"
                      style={{ display: "block", marginBottom: "5px" }}>
                      Account number
                    </label>
                    <Input
                      id="account-number"
                      disabled={isDisabled || readOnly}
                      value={jobDetail?.visitId}
                      onChange={e => setJobDetails({ ...jobDetail, visitId: e.target.value })}
                      className="input-field"
                      placeholder="Account number"
                      style={{ width: "100%" }}
                    />
                  </Col>

                  <Col xs={24} sm={12}>
                    <label
                      htmlFor="location"
                      className="input-label"
                      style={{ display: "block", marginBottom: "5px" }}>
                      Location
                    </label>
                    <Select
                      id="location"
                      variant="borderless"
                      disabled={isDisabled || readOnly}
                      className="input-field"
                      value={jobDetail?.locationId}
                      placeholder="Choose location"
                      maxTagCount="responsive"
                      showSearch
                      style={{ width: "100%" }}
                      onChange={value => setJobDetails({ ...jobDetail, locationId: value })}
                      getPopupContainer={trigger => trigger.parentElement || document.body}
                      filterOption={(input, option) =>
                        option?.props.children.toLowerCase().includes(input.toLowerCase())
                      }>
                      {locations?.map(location => (
                        <Select.Option key={location.locationId} value={location.locationId}>
                          {location.locationName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>

                  <Col xs={24} sm={12}>
                    <label
                      htmlFor="reporttype"
                      className="input-label"
                      style={{ display: "block", marginBottom: "5px" }}>
                      Report Type
                    </label>
                    <Select
                      id="reporttype"
                      variant="borderless"
                      disabled={isDisabled || readOnly}
                      className="input-field"
                      value={jobDetail?.reportType}
                      placeholder="Choose Report Type"
                      maxTagCount="responsive"
                      showSearch
                      style={{ width: "100%" }}
                      onChange={value => setJobDetails({ ...jobDetail, reportType: value })}
                      getPopupContainer={trigger => trigger.parentElement || document.body}
                      filterOption={(input, option) =>
                        option?.props.children.toLowerCase().includes(input.toLowerCase())
                      }>
                      {documentTypes?.map(documentType => (
                        <Select.Option
                          key={documentType.documentTypeId}
                          value={documentType.documentTypeName}>
                          {documentType.documentTypeName}
                        </Select.Option>
                      ))}
                    </Select>
                  </Col>

                  <Col span={24}>
                    <label
                      htmlFor="notes-to-client"
                      className="input-label"
                      style={{ display: "block", marginBottom: "5px" }}>
                      Notes to client
                    </label>
                    <TextArea
                      disabled={true}
                      style={{ height: "5.25rem", width: "100%" }}
                      value={jobDetail?.notesToClient}
                      onChange={e =>
                        setJobDetails({ ...jobDetail, notesToClient: e.target.value })
                      }
                      id="notes-to-client"
                      className="input-field"
                      placeholder="Notes to client"
                      autoSize={{ minRows: 3, maxRows: 3 }}
                    />
                  </Col>
                  <Col span={24}>
                    <label htmlFor="client-notes" className="input-label">
                      Client notes
                    </label>
                    <TextArea
                      disabled={isDisabled || readOnly}
                      id="client-notes"
                      value={jobDetail?.clientNotes}
                      onChange={e =>
                        setJobDetails({ ...jobDetail, clientNotes: e.target.value })
                      }
                      className="input-field"
                      style={{ height: "5.25rem" }}
                      placeholder="Notes from client"
                      autoSize={{ minRows: 3, maxRows: 3 }}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        ) : (
          <div>
            <Row gutter={[8, 16]} justify="space-between" align="middle">
              <Col xs={24} sm={16} style={{ padding: 0 }}>
                <Skeleton active paragraph={{ rows: 4 }} />
              </Col>

              <Col
                xs={24}
                sm={7}
                style={{ width: "56.25rem", height: "47.125rem", flexShrink: "0" }}>
                <div className="modal-actions">
                  <div className="button-container">
                    <Skeleton.Button active style={{ width: "120px", margin: "0 10px" }} />
                    <Skeleton.Button active style={{ width: "120px", margin: "0 10px" }} />
                  </div>
                  <Skeleton.Input active style={{ width: "40px" }} />
                </div>
                <Skeleton active paragraph={{ rows: 10 }} />
              </Col>
            </Row>
          </div>
        )}
      </Card>
    </div>
  );
};

export default DocumentEditorCard;
